/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import hexToRgba from 'hex-to-rgba';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { Button, CircularProgress } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import ChartCard from '~/components/ChartCard';
import IconWithTextCard from '~/components/IconWithTextCard';
import InfoCard from '~/components/InfoCard';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';
import Splash from '~/components/Splash/Inside';

import locations_icon from '~/assets/images/dashboard/location-icon-white.png';
import robots_icon from '~/assets/images/dashboard/robot-icon-white.png';
import {
    buildAreaSeriesByDayLast30DaysFromList,
    buildCleaningReportApiTimeRange,
    formatCleaningDuration,
    last30DaysCleaningReportDateRange,
    normalizeAggregatedCleaningReportResponse,
    summarizeList,
} from '~/pages/Platform/Robots/CleaningReportsDashboard/cleaningReportUtils';
import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';
import GetPluginspaceTheme from '~/util/PluginspaceTheme';

import checkPermission from '../../../util/CheckPermission';
import ActionsCard from './ActionsCard';
import AppIntegrationsCard from './AppIntegrationsCard';
import AutonomousActionsCard from './AutonomousActionsCard';
import DeliveryCard from './DeliveryCard';
import DisinfectionsCard from './DisinfectionsCard';
import InvitesCard from './InvitesCard';
import MessagesCard from './MessagesCard';
import MissionsCard from './MissionsCard';
import RC3OperationsCard from './RC3OperationsCard';
import { ChatCardContainer, Container } from './styles';
import TelepresenceCard from './TelepresenceCard';

const cardIcons = {
    robots: robots_icon,
    locations: locations_icon,
};

const timeCards = {
    rc3_operations: RC3OperationsCard,
    conversation: MessagesCard,
    actions: ActionsCard,
    autonomous_actions: AutonomousActionsCard,
    disinfection: DisinfectionsCard,
    invites: InvitesCard,
    telepresence: TelepresenceCard,
    delivery: DeliveryCard,
    integrations: AppIntegrationsCard,
    missions: MissionsCard,
};

const cardTypes = {
    color: IconWithTextCard,
    white: InfoCard,
};

/** Slug do tipo de robô na API (CC1 / limpeza Pudu). */
const ROBOT_TYPE_SLUG_CC1 = 'cc1';

function localeToBcp47(code) {
    if (!code) return 'pt-BR';
    const map = { pt_BR: 'pt-BR', en_US: 'en-US', en_GB: 'en-GB' };
    return map[code] || 'pt-BR';
}

function formatCleaningChartYAxisTick(v, unit) {
    if (v == null || Number.isNaN(Number(v))) return '';
    const n = Number(v);
    if (unit === 'seconds') return `${Math.round(n)} s`;
    if (unit === 'minutes') {
        return Number.isInteger(n) ? `${n} min` : `${n.toFixed(1)} min`;
    }
    return Number.isInteger(n) ? `${n} h` : `${n.toFixed(2)} h`;
}

export default function Dashboard() {
    const dispatch = useDispatch();
    const colors = GetPluginspaceTheme(useTheme());
    const settings = useSelector(state => state.settings || null);
    const { profile } = useSelector(state => state.user || null);
    const { signed } = useSelector(state => state.auth || {});

    const is_admin = checkPermission(profile);

    const { active } = settings;
    const active_group =
        active && active.id && active.id !== '*' ? active.id : null;
    const is_ready = is_admin || active_group;

    const dateSettings = {
        locale: settings.locale,
        date: { year: 'numeric', month: '2-digit', day: '2-digit' },
        label: { month: '2-digit', day: '2-digit' },
    };

    const userSettings = {
        colors,
        active,
    };

    const [dataCount, setDataCount] = useState({});
    const [dataValues, setDataValues] = useState({});

    const [isLoading, setIsLoading] = useState(true);

    const [cleaningPreviewLoading, setCleaningPreviewLoading] = useState(false);
    const [cleaningList, setCleaningList] = useState([]);

    const localeCode =
        (settings && settings.locale && settings.locale.code) || 'pt_BR';

    const {
        data: cleaningAreaSeries,
        unit: cleaningTimeChartUnit,
    } = useMemo(
        () =>
            buildAreaSeriesByDayLast30DaysFromList(
                cleaningList,
                localeToBcp47(localeCode)
            ),
        [cleaningList, localeCode]
    );

    const cleaningPreviewSummary = useMemo(() => summarizeList(cleaningList), [
        cleaningList,
    ]);
    const cleaningPreviewTotalLabel = formatCleaningDuration(
        cleaningPreviewSummary.totalCleanTimeSec
    );

    const cleaningGraphColor =
        colors.dashboard_graphs || colors.primary || '#3f51b5';

    const [tenantCards, setTenantCards] = useState([]);
    const pluginspaceId =
        settings && settings.pluginspace && settings.pluginspace.id;

    useEffect(() => {
        if (!pluginspaceId) return;

        api.get(`pluginspaces/${pluginspaceId}/cards_dashboards`)
            .then(res => {
                if (res.status === 200 && res.data && res.data.linked) {
                    const { linked } = res.data;
                    if (linked.length > 0) {
                        setTenantCards(linked.filter(c => c.active !== false));
                    } else {
                        const fallbackCards = (res.data.available || []).map(
                            (card, idx) => ({
                                uid: `fallback_${idx}`,
                                card_dashboard: card,
                                order: idx,
                            })
                        );
                        setTenantCards(fallbackCards);
                    }
                }
            })
            .catch(err => {
                console.error('Failed to load tenant cards', err);
                const defaultCardNames = Object.keys(timeCards);
                const fallbackCards = defaultCardNames.map((name, idx) => ({
                    uid: `fallback_${idx}`,
                    card_dashboard: {
                        name: `dashboard.card.${name}`,
                    },
                    order: idx,
                }));
                setTenantCards(fallbackCards);
            });
    }, [pluginspaceId]);

    function requestError(error) {
        if (error.response) {
            const message = (
                <LocaleMessage msg={`errors.${error.response.data.code}`} />
            );
            toast.error(message);
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            }
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
    }

    async function loadCounters() {
        if (is_ready) {
            setIsLoading(true);
            await api
                .get(`dashboards/counter`)
                .then(response => {
                    const { data } = response;
                    setDataCount({
                        robots: data.robots ? data.robots.count : 0,
                        locations: data.locations ? data.locations.count : 0,
                        applications: data.applications
                            ? data.applications.count
                            : 0,
                        tools: data.tools ? data.tools.count : 0,
                        appkeys: data.appkeys ? data.appkeys.count : 0,
                    });
                    setDataValues({
                        groups: data.groups ? data.groups.items : [],
                        robots: data.robots ? data.robots.items : [],
                        locations: data.locations ? data.locations.items : [],
                        dialogs: data.dialogs ? data.dialogs.items : [],
                        applications: data.applications
                            ? data.applications.items
                            : [],
                    });
                })
                .catch(error => requestError(error));
        }
        setIsLoading(false);
    }

    function getFilterInfo(values) {
        const infos = {};
        if (values) {
            values.forEach(m => {
                infos[m.id] = m.name;
            });
        }
        return infos;
    }

    useEffect(() => {
        loadCounters();
    }, [active, is_ready]);

    useEffect(() => {
        if (!signed || !is_ready) {
            setCleaningList([]);
            setCleaningPreviewLoading(false);
            return undefined;
        }

        let cancelled = false;

        api.get('robots/filter', {
            params: { type_slug: ROBOT_TYPE_SLUG_CC1 },
        })
            .then(res => {
                if (cancelled) return;
                const robots = Array.isArray(res.data) ? res.data : [];
                if (robots.length === 0) {
                    setCleaningList([]);
                    return;
                }
                setCleaningPreviewLoading(true);
                const range = last30DaysCleaningReportDateRange();
                const {
                    start_time,
                    end_time,
                } = buildCleaningReportApiTimeRange(
                    range.startDate,
                    range.endDate
                );
                api.get('robots/cleaning-report/cc1', {
                    params: { start_time, end_time },
                })
                    .then(reportRes => {
                        if (cancelled) return;
                        const payload = normalizeAggregatedCleaningReportResponse(
                            reportRes.data
                        );
                        setCleaningList(payload.list || []);
                    })
                    .catch(() => {
                        if (!cancelled) setCleaningList([]);
                    })
                    .finally(() => {
                        if (!cancelled) setCleaningPreviewLoading(false);
                    });
            })
            .catch(() => {
                if (!cancelled) {
                    setCleaningList([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [signed, is_ready, active && active.id]);

    function buildCard(card) {
        const { type } = card;
        const Card = cardTypes[type];

        return Card ? (
            <div className="col-md-3 col-12 dash-card" key={card.key}>
                <Link to={card.link}>
                    <Card
                        data={{
                            title: card.title,
                            subTitle: card.subtitle,
                        }}
                        color={card.color}
                        image={card.icon}
                        icon={card.icon}
                    />
                </Link>
            </div>
        ) : null;
    }

    function renderCleaningCard() {
        return (
            <div className="row card-row mb-5" key="cleaning_report_card">
                <div className="col-12 mb-5 dash-card">
                    <ChatCardContainer>
                        <ChartCard
                            className="animated slideInFromLeft animation-duration-3"
                            title={
                                <LocaleMessage msg="page.dashboard.cleaning_report_month_title" />
                            }
                            desc={
                                <LocaleMessage msg="page.dashboard.cleaning_report_month_subtitle" />
                            }
                            value={
                                cleaningPreviewLoading
                                    ? ' '
                                    : cleaningPreviewTotalLabel
                            }
                            icon="timer"
                        >
                            {cleaningPreviewLoading ? (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <CircularProgress color="primary" />
                                </div>
                            ) : (
                                <>
                                    <div className="area-chart-container mt-4 pt-2 mb-3 px-2">
                                        <ResponsiveContainer
                                            className="card-img-bottom overflow-hidden animated slideInFromLeft animation-duration-5"
                                            width="100%"
                                            height={300}
                                        >
                                            <AreaChart
                                                data={cleaningAreaSeries}
                                                margin={{
                                                    top: 24,
                                                    right: 8,
                                                    left: 10,
                                                    bottom: 10,
                                                }}
                                            >
                                                <CartesianGrid
                                                    vertical={false}
                                                />
                                                <XAxis
                                                    dataKey="date"
                                                    interval="preserveStartEnd"
                                                />
                                                <YAxis
                                                    width={56}
                                                    allowDecimals={
                                                        cleaningTimeChartUnit !==
                                                        'seconds'
                                                    }
                                                    tickFormatter={v =>
                                                        formatCleaningChartYAxisTick(
                                                            v,
                                                            cleaningTimeChartUnit
                                                        )
                                                    }
                                                />
                                                <Tooltip
                                                    formatter={(
                                                        v,
                                                        _n,
                                                        item
                                                    ) => {
                                                        const sec =
                                                            item &&
                                                            item.payload &&
                                                            item.payload
                                                                .secondsTotal;
                                                        const label =
                                                            sec != null
                                                                ? formatCleaningDuration(
                                                                      sec
                                                                  )
                                                                : formatCleaningChartYAxisTick(
                                                                      v,
                                                                      cleaningTimeChartUnit
                                                                  );
                                                        return [label, ''];
                                                    }}
                                                />
                                                <Area
                                                    dataKey="value"
                                                    stroke={cleaningGraphColor}
                                                    fill={`${hexToRgba(
                                                        cleaningGraphColor,
                                                        0.7
                                                    )}`}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <Link to="/cleaning-reports">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                        >
                                            <LocaleMessage msg="page.dashboard.view_more" />
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </ChartCard>
                    </ChatCardContainer>
                </div>
            </div>
        );
    }

    function buildTimeCard(type) {
        if (type === 'cleaning_report') {
            return renderCleaningCard();
        }

        const Card = timeCards[type];

        return Card ? (
            <Card
                dataValues={dataValues}
                dateSettings={dateSettings}
                userSettings={userSettings}
                requestError={error => requestError(error)}
                getFilterInfo={values => getFilterInfo(values)}
            />
        ) : null;
    }

    const counterCards = [
        {
            key: 'card_robots',
            type: 'color',
            link: '/robots',
            title: dataCount.robots || '0',
            subtitle: <LocaleMessage msg="page.dashboard.robots" />,
            color: colors.dashboard_cards,
            icon: cardIcons.robots,
        },
        {
            key: 'card_applications',
            type: 'white',
            link: '/applications',
            title: dataCount.applications || '0',
            subtitle: <LocaleMessage msg="page.dashboard.applications" />,
            color: colors.dashboard_cards,
            icon: 'play-circle',
        },
        {
            key: 'card_locations',
            type: 'color',
            link: active_group
                ? `/groups/${active_group}/locations`
                : '/pluginspace/locations',
            title: dataCount.locations || '0',
            subtitle: <LocaleMessage msg="page.dashboard.locations" />,
            color: colors.dashboard_cards,
            icon: cardIcons.locations,
        },
        {
            key: 'card_tools',
            type: 'white',
            link: '/tools',
            title: dataCount.tools || '0',
            subtitle: <LocaleMessage msg="page.dashboard.tools" />,
            color: colors.dashboard_cards,
            icon: 'wrench',
        },
        // {
        //     key: 'card_sections',
        //     type: 'white',
        //     link: active_group ? `/groups/${active_group}/sections` : '/groups',
        //     title: dataCount.sections || '0',
        //     subtitle: <LocaleMessage msg="page.dashboard.sections" />,
        //     color: colors.dashboard_cards,
        //     icon: 'accounts',
        // },
    ];

    function renderDashboardContent() {
        return (
            <>
                {active ? (
                    <h2>
                        {active.id === '*' ? (
                            <LocaleMessage msg="message.all_groups.title" />
                        ) : (
                            active.name.toUpperCase()
                        )}
                    </h2>
                ) : null}

                <div
                    className="animated slideInUpTiny animation-duration-3 mt-5 mb-5"
                    style={{ width: '100%' }}
                >
                    <div className="row card-row mb-5">
                        {counterCards.map(c => {
                            return buildCard(c);
                        })}
                    </div>

                    {tenantCards.map(item => {
                        const cardName =
                            item.card_dashboard && item.card_dashboard.name;
                        if (!cardName) return null;

                        const type = cardName.replace('dashboard.card.', '');

                        return (
                            <React.Fragment key={item.uid}>
                                {buildTimeCard(type)}
                            </React.Fragment>
                        );
                    })}
                </div>
            </>
        );
    }

    return (
        <>
            <PageContent
                title={<LocaleMessage msg="page.dashboard.title" />}
                currentPage={<LocaleMessage msg="page.dashboard.title" />}
                breadcrumbs={[]}
                loading={isLoading}
            >
                <Container>
                    {isLoading ? (
                        <Splash />
                    ) : (
                        <>{is_ready ? renderDashboardContent() : null}</>
                    )}
                </Container>
            </PageContent>
        </>
    );
}

Dashboard.propTypes = {};
